import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, ShieldOff, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ApiErrorDisplay } from '@/components/features/error';
import { useUser, useDisableUser, useEnableUser, useResetUserPassword } from '@/hooks/use-users';
import { useMerchant } from '@/hooks/use-merchants';
import type { AdminUser } from '@/types/user';

/** Status-Badge — gleiche Logik wie in der Listenseite */
function statusBadge(user: AdminUser) {
  if (!user.enabled) {
    return <Badge variant="secondary">Deaktiviert</Badge>;
  }
  switch (user.status) {
    case 'CONFIRMED':
      return <Badge variant="success">Aktiv</Badge>;
    case 'FORCE_CHANGE_PASSWORD':
      return <Badge variant="warning">Passwort-Wechsel</Badge>;
    case 'RESET_REQUIRED':
      return <Badge variant="warning">Reset ausstehend</Badge>;
    case 'UNCONFIRMED':
      return <Badge variant="secondary">Unbestaetigt</Badge>;
    default:
      return <Badge variant="outline">{user.status}</Badge>;
  }
}

/** Menschenlesbarer Rollen-Name */
function roleName(role: string): string {
  switch (role) {
    case 'platform_admin':
      return 'Platform Admin';
    case 'merchant_admin':
      return 'Merchant Admin';
    case 'merchant_viewer':
      return 'Merchant Viewer';
    default:
      return role;
  }
}

export function UserDetailPage() {
  const { sub } = useParams<{ sub: string }>();
  const navigate = useNavigate();

  const { data: user, isLoading, error, refetch } = useUser(sub);
  const disableMutation = useDisableUser();
  const enableMutation = useEnableUser();
  const resetPwMutation = useResetUserPassword();

  // Merchant-Name aufloesen, falls vorhanden
  const { data: merchant } = useMerchant(user?.merchantId ?? undefined);

  if (!sub) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Fehlende Benutzer-ID</p>
        <Button variant="link" onClick={() => navigate('/users')}>
          Zurueck zur Liste
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ApiErrorDisplay error={error} onRetry={() => refetch()} />;
  }

  if (!user) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Benutzer nicht gefunden.</p>
        <Button variant="link" onClick={() => navigate('/users')}>
          Zurueck zur Liste
        </Button>
      </div>
    );
  }

  const handleDisable = async () => {
    try {
      await disableMutation.mutateAsync(user.sub);
      toast.success('Benutzer deaktiviert.');
    } catch (err) {
      toast.error(String(err));
    }
  };

  const handleEnable = async () => {
    try {
      await enableMutation.mutateAsync(user.sub);
      toast.success('Benutzer aktiviert.');
    } catch (err) {
      toast.error(String(err));
    }
  };

  const handleResetPassword = async () => {
    try {
      await resetPwMutation.mutateAsync(user.sub);
      toast.success('Passwort wurde zurückgesetzt. Der Benutzer muss sich ein neues Passwort setzen.');
    } catch (err) {
      toast.error(String(err));
    }
  };

  const merchantDisplay = user.merchantId
    ? (merchant ? `${merchant.name} (${user.merchantId})` : user.merchantId)
    : '—';

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Benutzer-Details</h1>
          <p className="mt-1 font-mono text-sm text-muted-foreground">{user.email}</p>
        </div>
        <div className="flex gap-2">
          {/* Aktivieren / Deaktivieren Toggle */}
          {user.enabled ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  size="sm"
                  loading={disableMutation.isPending}
                >
                  <ShieldOff className="mr-2 h-4 w-4" />
                  Deaktivieren
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Benutzer deaktivieren?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {user.email} kann sich nach der Deaktivierung nicht mehr anmelden.
                    Der Account bleibt erhalten und kann jederzeit reaktiviert werden.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDisable}>
                    Deaktivieren
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={handleEnable}
              loading={enableMutation.isPending}
            >
              <ShieldCheck className="mr-2 h-4 w-4" />
              Aktivieren
            </Button>
          )}

          {/* Passwort zuruecksetzen */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                loading={resetPwMutation.isPending}
              >
                <KeyRound className="mr-2 h-4 w-4" />
                Passwort zuruecksetzen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Passwort zuruecksetzen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Das Passwort von {user.email} wird zurückgesetzt. Beim nächsten Login muss ein neues Passwort vergeben werden.
                  Das aktuelle Passwort wird ungueltig.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={handleResetPassword}>
                  Zuruecksetzen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button variant="outline" size="sm" onClick={() => navigate('/users')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurueck
          </Button>
        </div>
      </div>

      {/* Detail Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Benutzerinformationen</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">E-Mail</dt>
              <dd className="mt-1 text-sm">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Rolle</dt>
              <dd className="mt-1 text-sm">{roleName(user.role)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Merchant</dt>
              <dd className="mt-1 text-sm font-mono">{merchantDisplay}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Status</dt>
              <dd className="mt-1">{statusBadge(user)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Cognito Sub</dt>
              <dd className="mt-1 text-sm font-mono text-muted-foreground">{user.sub}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Erstellt</dt>
              <dd className="mt-1 text-sm">
                {new Date(user.createdAt).toLocaleString('de-DE')}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
